import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { Event, eventApi } from '../../../../../services/eventService';

interface EditEventModalProps {
  show: boolean;
  onHide: () => void;
  event: Event | null;
  onEventUpdated: (updatedEvent: Event) => void;
}

interface EventFormData {
  title: string;
  sub_title: string;
  register_start_date: string;
  register_end_date: string;
  vote_end_date: string;
  event_date: string;
  min_select_users: number;
  max_select_users: number;
  round: number;
  group_size: number;
  status: number;
  notification_enabled: boolean;
  location_name: string;
  street: string;
  house_number: string;
  postal_code: string;
  city: string;
  country: string;
  Branding: string;
}

const EditEventModal: React.FC<EditEventModalProps> = ({ 
  show, 
  onHide, 
  event, 
  onEventUpdated 
}) => {
  const [formData, setFormData] = useState<EventFormData>({
    title: '',
    sub_title: '',
    register_start_date: '',
    register_end_date: '',
    vote_end_date: '',
    event_date: '',
    min_select_users: 1,
    max_select_users: 10,
    round: 1,
    group_size: 2,
    status: 1,
    notification_enabled: false,
    location_name: '',
    street: '',
    house_number: '',
    postal_code: '',
    city: '',
    country: '',
    Branding: 'PartnerForum'
  });

  const [isLoading, setIsLoading] = useState(false);

  // Initialize form with event data when modal opens
  useEffect(() => {
    if (show && event) {
      setFormData({
        title: event.title || '',
        sub_title: event.sub_title || '',
        register_start_date: formatDateTimeLocal(event.register_start_date),
        register_end_date: formatDateTimeLocal(event.register_end_date),
        vote_end_date: formatDateTimeLocal(event.vote_end_date),
        event_date: formatDateTimeLocal(event.event_date),
        min_select_users: event.min_select_users,
        max_select_users: event.max_select_users,
        round: event.round,
        group_size: event.group_size || 2,
        status: event.status || 1,
        notification_enabled: event.notification_enabled || false,
        location_name: event.location_name || '',
        street: event.street || '',
        house_number: event.house_number || '',
        postal_code: event.postal_code || '',
        city: event.city || '',
        country: event.country || '',
        Branding: event.Branding || 'PartnerForum'
      });
    }
  }, [show, event]);

  // Format datetime for input field
  const formatDateTimeLocal = (dateString: string): string => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };

  // Handle form input changes
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' 
        ? (e.target as HTMLInputElement).checked
        : type === 'number' 
          ? parseFloat(value) || 0
          : value
    }));
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!event) {
      toast.error('Event data not available');
      return;
    }

    setIsLoading(true);

    try {
      // Convert datetime-local format to ISO string
      const updateData = {
        ...formData,
        register_start_date: new Date(formData.register_start_date).toISOString(),
        register_end_date: new Date(formData.register_end_date).toISOString(),
        vote_end_date: new Date(formData.vote_end_date).toISOString(),
        event_date: new Date(formData.event_date).toISOString(),
      };

      const updatedEvent = await eventApi.updateEvent(event.Events_ID, updateData);
      
      // Update the local form data with the response from the server
      setFormData({
        title: updatedEvent.title || '',
        sub_title: updatedEvent.sub_title || '',
        register_start_date: formatDateTimeLocal(updatedEvent.register_start_date),
        register_end_date: formatDateTimeLocal(updatedEvent.register_end_date),
        vote_end_date: formatDateTimeLocal(updatedEvent.vote_end_date),
        event_date: formatDateTimeLocal(updatedEvent.event_date),
        min_select_users: updatedEvent.min_select_users,
        max_select_users: updatedEvent.max_select_users,
        round: updatedEvent.round,
        group_size: updatedEvent.group_size || 2,
        status: updatedEvent.status || 1,
        notification_enabled: updatedEvent.notification_enabled || false,
        location_name: updatedEvent.location_name || '',
        street: updatedEvent.street || '',
        house_number: updatedEvent.house_number || '',
        postal_code: updatedEvent.postal_code || '',
        city: updatedEvent.city || '',
        country: updatedEvent.country || '',
        Branding: updatedEvent.Branding || 'PartnerForum'
      });
      
      toast.success('Event updated successfully!');
      onEventUpdated(updatedEvent);
      onHide();
      
    } catch (error) {
      console.error('Error updating event:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to update event');
    } finally {
      setIsLoading(false);
    }
  };

  if (!show) return null;

  return (
    <div className="modal show d-block" tabIndex={-1}>
      <div className="modal-dialog modal-lg">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">Edit Event</h5>
            <button
              type="button"
              className="btn-close"
              onClick={onHide}
            ></button>
          </div>
          <div className="modal-body">
            <form onSubmit={handleSubmit}>
              <div className="row mb-3">
                <div className="col-md-6">
                  <label htmlFor="title" className="form-label">Title *</label>
                  <input
                    type="text"
                    className="form-control"
                    id="title"
                    name="title"
                    value={formData.title}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                <div className="col-md-6">
                  <label htmlFor="sub_title" className="form-label">Subtitle</label>
                  <input
                    type="text"
                    className="form-control"
                    id="sub_title"
                    name="sub_title"
                    value={formData.sub_title}
                    onChange={handleInputChange}
                  />
                </div>
              </div>

              <div className="row mb-3">
                <div className="col-md-6">
                  <label htmlFor="register_start_date" className="form-label">Registration Start *</label>
                  <input
                    type="datetime-local"
                    className="form-control"
                    id="register_start_date"
                    name="register_start_date"
                    value={formData.register_start_date}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                <div className="col-md-6">
                  <label htmlFor="register_end_date" className="form-label">Registration End *</label>
                  <input
                    type="datetime-local"
                    className="form-control"
                    id="register_end_date"
                    name="register_end_date"
                    value={formData.register_end_date}
                    onChange={handleInputChange}
                    required
                  />
                </div>
              </div>

              <div className="row mb-3">
                <div className="col-md-6">
                  <label htmlFor="vote_end_date" className="form-label">Vote End Date *</label>
                  <input
                    type="datetime-local"
                    className="form-control"
                    id="vote_end_date"
                    name="vote_end_date"
                    value={formData.vote_end_date}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                <div className="col-md-6">
                  <label htmlFor="event_date" className="form-label">Event Date *</label>
                  <input
                    type="datetime-local"
                    className="form-control"
                    id="event_date"
                    name="event_date"
                    value={formData.event_date}
                    onChange={handleInputChange}
                    required
                  />
                </div>
              </div>

              <div className="row mb-3">
                <div className="col-md-4">
                  <label htmlFor="min_select_users" className="form-label">Min Users *</label>
                  <input
                    type="number"
                    className="form-control"
                    id="min_select_users"
                    name="min_select_users"
                    value={formData.min_select_users}
                    onChange={handleInputChange}
                    min="1"
                    required
                  />
                </div>
                <div className="col-md-4">
                  <label htmlFor="max_select_users" className="form-label">Max Users *</label>
                  <input
                    type="number"
                    className="form-control"
                    id="max_select_users"
                    name="max_select_users"
                    value={formData.max_select_users}
                    onChange={handleInputChange}
                    min="1"
                    required
                  />
                </div>
                <div className="col-md-4">
                  <label htmlFor="round" className="form-label">Rounds *</label>
                  <input
                    type="number"
                    className="form-control"
                    id="round"
                    name="round"
                    value={formData.round}
                    onChange={handleInputChange}
                    min="1"
                    required
                  />
                </div>
              </div>

              <div className="row mb-3">
                <div className="col-md-4">
                  <label htmlFor="status" className="form-label">Status</label>
                  <select
                    className="form-select"
                    id="status"
                    name="status"
                    value={formData.status}
                    onChange={handleInputChange}
                  >
                    <option value={1}>Active</option>
                    <option value={0}>Inactive</option>
                  </select>
                </div>
                <div className="col-md-4">
                  <label htmlFor="group_size" className="form-label">Group Size</label>
                  <input
                    type="number"
                    className="form-control"
                    id="group_size"
                    name="group_size"
                    value={formData.group_size}
                    onChange={handleInputChange}
                    min="2"
                  />
                </div>
                <div className="col-md-4">
                  <label htmlFor="Branding" className="form-label">Branding</label>
                  <select
                    className="form-select"
                    id="Branding"
                    name="Branding"
                    value={formData.Branding}
                    onChange={handleInputChange}
                  >
                    <option value="PartnerForum">PartnerForum</option>
                    <option value="MatchMaker">MatchMaker</option>
                  </select>
                </div>
              </div>

              <div className="mb-3">
                <label htmlFor="location_name" className="form-label">Location Name</label>
                <input
                  type="text"
                  className="form-control"
                  id="location_name"
                  name="location_name"
                  value={formData.location_name}
                  onChange={handleInputChange}
                />
              </div>

              <div className="row mb-3">
                <div className="col-md-8">
                  <label htmlFor="street" className="form-label">Street</label>
                  <input
                    type="text"
                    className="form-control"
                    id="street"
                    name="street"
                    value={formData.street}
                    onChange={handleInputChange}
                  />
                </div>
                <div className="col-md-4">
                  <label htmlFor="house_number" className="form-label">House Number</label>
                  <input
                    type="text"
                    className="form-control"
                    id="house_number"
                    name="house_number"
                    value={formData.house_number}
                    onChange={handleInputChange}
                  />
                </div>
              </div>

              <div className="row mb-3">
                <div className="col-md-4">
                  <label htmlFor="postal_code" className="form-label">Postal Code</label>
                  <input
                    type="text"
                    className="form-control"
                    id="postal_code"
                    name="postal_code"
                    value={formData.postal_code}
                    onChange={handleInputChange}
                  />
                </div>
                <div className="col-md-4">
                  <label htmlFor="city" className="form-label">City</label>
                  <input
                    type="text"
                    className="form-control"
                    id="city"
                    name="city"
                    value={formData.city}
                    onChange={handleInputChange}
                  />
                </div>
                <div className="col-md-4">
                  <label htmlFor="country" className="form-label">Country</label>
                  <input
                    type="text"
                    className="form-control"
                    id="country"
                    name="country"
                    value={formData.country}
                    onChange={handleInputChange}
                  />
                </div>
              </div>

              <div className="form-check mb-3">
                <input
                  type="checkbox"
                  className="form-check-input"
                  id="notification_enabled"
                  name="notification_enabled"
                  checked={formData.notification_enabled}
                  onChange={handleInputChange}
                />
                <label className="form-check-label" htmlFor="notification_enabled">
                  Enable Notifications
                </label>
              </div>
            </form>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onHide}>
              Cancel
            </button>
            <button type="button" className="btn btn-primary" disabled={isLoading} onClick={handleSubmit}>
              {isLoading ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditEventModal;
